
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Review } from '@/types/review';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { 
  Check, 
  Flag, 
  MessageSquare, 
  MoreHorizontal, 
  Star, 
  Trash2, 
  X 
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AddResponseDialog } from './AddResponseDialog';

interface ReviewActionsProps {
  review: Review;
}

export function ReviewActions({ review }: ReviewActionsProps) {
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResponseDialogOpen, setIsResponseDialogOpen] = useState(false);
  
  const updateApproval = async (isApproved: boolean) => {
    try {
      const { error } = await supabase.rpc('update_review_approval', {
        p_review_id: review.id,
        p_is_approved: isApproved
      });
      
      if (error) throw error;
      
      toast.success(`Review ${isApproved ? 'approved' : 'unapproved'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    } catch (error) {
      toast.error(`Failed to update review: ${(error as Error).message}`);
    }
  };
  
  const updateFeatured = async (isFeatured: boolean) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ is_featured: isFeatured })
        .eq('id', review.id);
      
      if (error) throw error;
      
      toast.success(`Review ${isFeatured ? 'featured' : 'unfeatured'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    } catch (error) {
      toast.error(`Failed to update review: ${(error as Error).message}`);
    }
  };
  
  const updateFlagged = async (isFlagged: boolean) => {
    try {
      const { error } = await supabase
        .from('reviews')
        .update({ is_flagged: isFlagged })
        .eq('id', review.id);
      
      if (error) throw error;
      
      toast.success(`Review ${isFlagged ? 'flagged' : 'unflagged'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    } catch (error) {
      toast.error(`Failed to update review: ${(error as Error).message}`);
    }
  };
  
  const deleteReview = async () => {
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', review.id);
      
      if (error) throw error;
      
      toast.success('Review deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    } catch (error) {
      toast.error(`Failed to delete review: ${(error as Error).message}`);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => updateApproval(!review.is_approved)}>
            {review.is_approved ? (
              <>
                <X className="mr-2 h-4 w-4" />
                <span>Unapprove</span>
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                <span>Approve</span>
              </>
            )}
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => updateFeatured(!review.is_featured)}>
            <Star className="mr-2 h-4 w-4" />
            <span>{review.is_featured ? 'Unfeature' : 'Feature'}</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => updateFlagged(!review.is_flagged)}>
            <Flag className="mr-2 h-4 w-4" />
            <span>{review.is_flagged ? 'Unflag' : 'Flag'}</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={() => setIsResponseDialogOpen(true)}>
            <MessageSquare className="mr-2 h-4 w-4" />
            <span>{review.admin_response ? 'Edit Response' : 'Add Response'}</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={() => setIsDeleteDialogOpen(true)}
            className="text-red-600 focus:text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this review?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the review.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteReview} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <AddResponseDialog 
        review={review}
        open={isResponseDialogOpen}
        onOpenChange={setIsResponseDialogOpen}
      />
    </>
  );
}
